import React from "react";

import type { LaneRendererProps, SignalLaneItem } from "../lane.types";
import { itemVisualDensity, productTone, readNumberMeta, readStringMeta, visibleLaneItems } from "../product/rendering";

function stateChip(label: string, tone: "cyan" | "amber" | "slate") {
  const className =
    tone === "cyan"
      ? "border-cyan-300/18 bg-cyan-200/90 text-cyan-950"
      : tone === "amber"
        ? "border-amber-700/16 bg-amber-50/92 text-amber-900"
        : "border-slate-700/16 bg-slate-50/92 text-slate-900";

  return <span className={["rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]", className].join(" ")}>{label}</span>;
}

function summarizeSignalBands(item: SignalLaneItem) {
  return item.bands.slice(0, 4).map((band) => ({
    key: band.key,
    peak: band.points.reduce((max, point) => Math.max(max, Math.abs(point.value)), 0),
  }));
}

export default function SignalLaneRenderer({ lane, items, viewport }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);

  return (
    <div className="relative h-full min-h-[48px]" data-lane-class="signal" data-lane-id={lane.id} data-lane-items={items.length}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-cyan-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const label = item.itemType === "signal" ? lane.title : item.id;
          const signalItem = item.itemType === "signal" ? item : null;
          const bandSummary = signalItem ? summarizeSignalBands(signalItem) : [];
          const sampleRate = readNumberMeta(item, "sampleRate");
          const status = readStringMeta(item, "status") ?? readStringMeta(item, "analysisState");

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-cyan-300/24 bg-[linear-gradient(135deg,rgba(34,211,238,0.16),rgba(8,145,178,0.14),rgba(2,6,23,0.22))] px-3 shadow-[0_8px_20px_rgba(34,211,238,0.10)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="absolute inset-x-2 bottom-[6px] flex h-5 items-end gap-[2px] opacity-85">
                {(bandSummary.length > 0 ? bandSummary : [{ key: "signal", peak: 0.42 }]).map((band, index) => (
                  <div key={`${band.key}-${index}`} className="min-w-0 flex-1 rounded-full bg-cyan-100/28" style={{ height: `${Math.max(24, Math.round(Math.min(1, band.peak) * 100))}%` }} />
                ))}
              </div>
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {density !== "micro" ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-cyan-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {status ? stateChip(status, status === "failed" ? "amber" : "cyan") : null}
                        {sampleRate ? stateChip(`${Math.round(sampleRate)}hz`, "slate") : null}
                        {signalItem?.bands?.[0]?.key ? stateChip(signalItem.bands[0].key, "cyan") : null}
                      </div>
                    </>
                  ) : (
                    <div className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-50/88">Sig</div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="grid h-full place-items-center px-3">
          <div className={["rounded-full border px-3 py-1 text-[10px] font-semibold", tone.emptyClassName].join(" ")}>No signals in view</div>
        </div>
      )}
    </div>
  );
}